import { createContext, useState } from "react";
import VerifAuthorizationDialog from "../dialogs/VerifAuthorizationDialog";
import useIsMobile from "../hooks/useIsMobile";

// Shades picked to stay readable (>= 4.5:1 on white) for older users
const MOBILE_TOAST_VARIANTS = {
  success: {
    icon: "pi-check",
    color: "#15803d",
    titleColor: "#166534",
  },
  error: {
    icon: "pi-times",
    color: "#b91c1c",
    titleColor: "#991b1b",
  },
};

const UiContext = createContext({
  toastRef: null,
  setToastRef: function (toastRef) { },
  showSuccessMsg: function (title, message) { },
  showErrorMsg: function (title, message) { },
  showVerifAuthDialog: function (props) { },
  hideVerifAuthDialog: function () { },
  visibleVerifAuthDialog: null,
  propsVerifAuthDialog: null,
});

const UiContextProvider = (props) => {
  const isMobile = useIsMobile();
  const [tRef, setTRef] = useState(null);

  const [isVisiblVerifAuthDialog, setIsVisiblVerifAuthDialog] = useState(false);
  const [dialogVerifAuthDialogProps, setDialogVerifAuthDialogProps] = useState(
    {}
  );

  const setToastRefHandler = (toastRef) => {
    setTRef(toastRef);
  };

  // Compact toast for small screens: icon + title + detail, right-aligned like desktop, tap to close
  const showMobileToast = (severity, title, message) => {
    const { icon, color, titleColor } = MOBILE_TOAST_VARIANTS[severity];

    tRef.current.show({
      severity,
      life: 3000,
      closable: false,
      // The toast container is offset 20px from the right edge, so pull the
      // message back to leave an even 16px gutter on both sides. The default
      // PrimeReact message chrome is cleared; the card below draws its own.
      style: {
        width: "calc(100vw - 2rem)",
        marginLeft: "auto",
        marginRight: "-4px",
        background: "transparent",
        border: "none",
        boxShadow: "none",
        backdropFilter: "none",
      },
      contentStyle: {
        border: "none",
        padding: 0,
        background: "transparent",
        width: "100%",
        justifyContent: "flex-end",
      },
      content: (props) => (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "0.5rem",
            width: "fit-content",
            minWidth: "min(16rem, 100%)",
            maxWidth: "100%",
            padding: "0.75rem 1rem",
            background: "var(--surface-0, #ffffff)",
            borderLeft: `4px solid ${color}`,
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          }}
          onClick={() => tRef.current.remove(props.message)}
        >
          <i
            className={`pi ${icon}`}
            style={{ color, fontSize: "1.5rem", flexShrink: 0 }}
          />
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <span style={{ fontWeight: 700, fontSize: "1rem", color: titleColor }}>
              {title}
            </span>
            {message && (
              <span
                style={{
                  fontSize: "0.95rem",
                  color,
                  wordBreak: "break-word",
                }}
              >
                {message}
              </span>
            )}
          </div>
        </div>
      ),
    });
  };

  const showSuccessMsgHandler = (title, message) => {
    if (tRef) {
      if (isMobile) {
        showMobileToast("success", title, message);
        return;
      }
      tRef.current.show({
        severity: "success",
        summary: title,
        detail: message,
        life: 3000,
      });
    }
  };

  const showErrorMsgHandler = (title, message) => {
    if (tRef) {
      if (isMobile) {
        showMobileToast("error", title, message);
        return;
      }
      tRef.current.show({
        severity: "error",
        summary: title,
        detail: message,
        life: 3000,
      });
    }
  };

  const showVerifAuthDialogHandler = (props) => {
    setDialogVerifAuthDialogProps(props);
    setIsVisiblVerifAuthDialog(true);
  };

  const hideVerifAuthDialogHandler = () => {
    setIsVisiblVerifAuthDialog(false);
  };

  const context = {
    toastRef: tRef,
    setToastRef: setToastRefHandler,
    showSuccessMsg: showSuccessMsgHandler,
    showErrorMsg: showErrorMsgHandler,
    showVerifAuthDialog: showVerifAuthDialogHandler,
    hideVerifAuthDialog: hideVerifAuthDialogHandler,
    visibleVerifAuthDialog: isVisiblVerifAuthDialog,
    propsVerifAuthDialog: dialogVerifAuthDialogProps,
  };

  return (
    <UiContext.Provider value={context}>{props.children}</UiContext.Provider>
  );
};

export { UiContextProvider };

export default UiContext;
